import { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { ArrowRight, Bookmark, BookOpen, Heart, Lock, ShieldCheck } from "lucide-react";
import { STORIES, chapterLabel } from "../data/book";
import { useLang } from "../i18n";
import { useAuth } from "../auth";
import api, { formatApiError } from "../api";
import Reveal from "../components/Reveal";

const fmtAmount = (cents, cur = "eur") => `${(cents / 100).toFixed(2)} ${cur === "eur" ? "€" : cur}`;

const Dashboard = () => {
  const { t, lang } = useLang();
  const { user, checkAuth } = useAuth();
  const [data, setData] = useState(null);
  const [pwForm, setPwForm] = useState({ current: "", next: "" });
  const [emailForm, setEmailForm] = useState({ email: "", password: "" });
  const [pwMsg, setPwMsg] = useState(null);
  const [emailMsg, setEmailMsg] = useState(null);
  const [emailPending, setEmailPending] = useState(false);
  const [emailCode, setEmailCode] = useState("");
  const [recEmail, setRecEmail] = useState("");
  const [recCode, setRecCode] = useState("");
  const [recAwaiting, setRecAwaiting] = useState(false);
  const [recMsg, setRecMsg] = useState(null);
  const [phone, setPhone] = useState("");
  const [phoneMsg, setPhoneMsg] = useState(null);
  const [sqPicks, setSqPicks] = useState([]);
  const [sqAnswers, setSqAnswers] = useState({});
  const [sqMsg, setSqMsg] = useState(null);

  const SQ_KEYS = ["q_first_club", "q_first_vinyl", "q_childhood_street", "q_best_friend", "q_first_concert", "q_mother_name", "q_first_school", "q_nickname"];

  const sendRecCode = async () => {
    setRecMsg(null);
    try {
      await api.post("/me/recovery-email", { email: recEmail });
      setRecAwaiting(true);
      setRecMsg({ ok: true, text: t("dash_code_sent") });
    } catch (err) { setRecMsg({ ok: false, text: formatApiError(err) }); }
  };

  const verifyRecCode = async () => {
    setRecMsg(null);
    try {
      await api.post("/me/recovery-email/verify", { code: recCode });
      setRecMsg({ ok: true, text: t("dash_saved") });
      setRecAwaiting(false); setRecCode(""); setRecEmail("");
      api.get("/me/dashboard").then(({ data }) => setData(data));
    } catch (err) { setRecMsg({ ok: false, text: formatApiError(err) }); }
  };

  const removeRecEmail = async () => {
    await api.delete("/me/recovery-email");
    setData((d) => ({ ...d, recovery_email: null }));
  };

  const savePhone = async () => {
    setPhoneMsg(null);
    try {
      await api.post("/me/phone", { phone });
      setPhoneMsg({ ok: true, text: t("dash_saved") });
      setData((d) => ({ ...d, phone }));
    } catch (err) { setPhoneMsg({ ok: false, text: formatApiError(err) }); }
  };

  const removePhone = async () => {
    await api.delete("/me/phone");
    setData((d) => ({ ...d, phone: null }));
    setPhone("");
  };

  const toggleSq = (k) => {
    setSqPicks((p) => (p.includes(k) ? p.filter((x) => x !== k) : p.length < 5 ? [...p, k] : p));
  };

  const saveSq = async () => {
    setSqMsg(null);
    try {
      await api.post("/me/security-questions", { answers: sqAnswers });
      setSqMsg({ ok: true, text: t("dash_saved") });
      setData((d) => ({ ...d, security_set: true }));
    } catch (err) { setSqMsg({ ok: false, text: formatApiError(err) }); }
  };

  useEffect(() => {
    if (!user) return;
    api.get("/me/dashboard").then(({ data }) => setData(data)).catch(() => {});
  }, [user]);

  if (user === false) return <Navigate to="/accedi?return=/dashboard" replace />;

  const changePw = async (e) => {
    e.preventDefault();
    setPwMsg(null);
    try {
      await api.post("/auth/change-password", { current_password: pwForm.current, new_password: pwForm.next });
      setPwMsg({ ok: true, text: t("dash_pw_ok") });
      setPwForm({ current: "", next: "" });
    } catch (err) {
      setPwMsg({ ok: false, text: formatApiError(err) });
    }
  };

  const changeEmail = async (e) => {
    e.preventDefault();
    setEmailMsg(null);
    try {
      await api.post("/auth/change-email", { new_email: emailForm.email, password: emailForm.password });
      setEmailPending(true);
      setEmailMsg({ ok: true, text: t("dash_email_pending") });
    } catch (err) {
      setEmailMsg({ ok: false, text: formatApiError(err) });
    }
  };

  const confirmEmail = async (e) => {
    e.preventDefault();
    setEmailMsg(null);
    try {
      await api.post("/auth/change-email/confirm", { code: emailCode });
      setEmailPending(false);
      setEmailCode("");
      setEmailForm({ email: "", password: "" });
      setEmailMsg({ ok: true, text: t("dash_email_ok") });
      checkAuth();
    } catch (err) {
      setEmailMsg({ ok: false, text: formatApiError(err) });
    }
  };

  const progressEntries = Object.entries(data?.progress || {}).sort((a, b) => Number(a[0]) - Number(b[0]));
  const lastStory = data?.last != null ? STORIES.find((s) => s.id === data.last) : null;
  const field = "w-full rounded-lg border border-white/15 bg-black/40 px-4 py-3 text-sm placeholder:text-mute focus:border-ember focus:outline-none transition-colors";

  return (
    <main className="pt-16" data-testid="dashboard-page">
      <section className="max-w-5xl mx-auto px-5 sm:px-8 pt-16 pb-24">
        <Reveal>
          <div className="flex flex-wrap items-center justify-between gap-4 mb-3">
            <h1 className="font-display font-extrabold text-3xl sm:text-5xl tracking-tight" data-testid="dashboard-title">
              {t("dash_hello")}{user?.name ? `, ${user.name.split(" ")[0]}` : ""}
            </h1>
            {data?.user?.role === "admin" && (
              <Link to="/bacheca" data-testid="dashboard-admin-link" className="inline-flex items-center gap-2 rounded-full border border-gold/50 px-5 py-2.5 text-xs font-mono2 tracking-[0.2em] uppercase text-gold hover:bg-gold hover:text-black transition-colors">
                {t("dash_admin_link")} <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            )}
          </div>
          <p className="text-mute text-sm mb-10 flex items-center gap-2">
            <ShieldCheck className="w-4 h-4 text-viola" /> {t("dash_reader_sub")}
          </p>
        </Reveal>

        <Reveal>
          <div className="rounded-2xl border border-white/10 bg-card p-6 mb-8" data-testid="dashboard-unlock-status">
            {data?.access && (
              <>
                <div className="flex flex-wrap items-center justify-between gap-4 mb-5">
                  <div>
                    <p className="font-mono2 text-[10px] tracking-[0.3em] uppercase text-mute mb-1.5">{t("dash_level_title")}</p>
                    <p className="flex items-center gap-2 font-display font-semibold text-lg" data-testid="dashboard-level-label">
                      {data.access.level === "full"
                        ? <><BookOpen className="w-5 h-5 text-gold" /> <span className="text-gold">{t("dash_level_full")}</span></>
                        : <><Lock className="w-5 h-5 text-ember" /> <span className="text-mist">{t(`dash_level_${data.access.level}`)}</span></>}
                    </p>
                    <p className="text-sm text-mute mt-1.5" data-testid="dashboard-level-desc">
                      {data.access.level === "full" ? t("dash_level_full_desc")
                        : data.access.level === "partial" ? t("dash_level_partial_desc").replace("{n}", data.access.accessible)
                        : t("dash_level_registered_desc")}
                    </p>
                  </div>
                  {data.access.level !== "full" && (
                    <Link to="/storia/6" data-testid="dashboard-unlock-cta" className="rounded-full bg-ember px-6 py-2.5 text-xs font-semibold uppercase tracking-wider text-black hover:bg-gold transition-colors">
                      {t("dash_unlock_cta")}
                    </Link>
                  )}
                </div>
                <div className="flex items-center gap-3">
                  <div className="flex-1 h-2 rounded-full bg-white/10 overflow-hidden">
                    <div className="h-full rounded-full bg-gradient-to-r from-ember to-gold transition-all duration-700" style={{ width: `${Math.round((data.access.accessible / data.access.total) * 100)}%` }} />
                  </div>
                  <span className="font-mono2 text-xs text-mute whitespace-nowrap" data-testid="dashboard-access-count">
                    {data.access.accessible}/{data.access.total} {t("dash_level_chapters")}
                  </span>
                </div>
              </>
            )}
          </div>
        </Reveal>

        {lastStory && (
          <Reveal>
            <Link to={`/storia/${lastStory.id}?v=read`} data-testid="dashboard-continue"
              className="group flex items-center justify-between gap-4 rounded-2xl border border-gold/40 bg-gold/5 px-6 py-5 mb-10 hover:bg-gold/10 transition-colors">
              <div>
                <p className="font-mono2 text-[10px] tracking-[0.3em] uppercase text-gold mb-1">{t("continue_reading")}</p>
                <p className="font-display font-bold text-lg">{chapterLabel(lastStory, lang)}</p>
              </div>
              <ArrowRight className="w-5 h-5 text-gold transition-transform group-hover:translate-x-1.5" />
            </Link>
          </Reveal>
        )}

        <div className="grid lg:grid-cols-2 gap-8 mb-10">
          <Reveal>
            <h2 className="font-display font-bold text-xl mb-4">{t("dash_chapters")}</h2>
            <div className="rounded-xl border border-white/10 bg-card p-5 space-y-3" data-testid="dashboard-chapters">
              {progressEntries.length === 0 && <p className="text-mute text-sm">{t("dash_empty_reading")}</p>}
              {progressEntries.map(([cid, p]) => {
                const s = STORIES.find((x) => x.id === Number(cid));
                if (!s) return null;
                return (
                  <Link key={cid} to={`/storia/${cid}?v=read`} className="flex items-center gap-3 group" data-testid={`dashboard-chapter-${cid}`}>
                    <span className="font-mono2 text-xs text-ember w-16 shrink-0">{chapterLabel(s, lang)}</span>
                    <div className="flex-1 h-1.5 rounded-full bg-white/10">
                      <div className="h-full rounded-full bg-gradient-to-r from-ember to-gold" style={{ width: `${p.percent}%` }} />
                    </div>
                    <span className="font-mono2 text-[10px] text-mute w-10 text-right">{p.percent}%</span>
                  </Link>
                );
              })}
            </div>
          </Reveal>

          <Reveal delay={0.08}>
            <h2 className="font-display font-bold text-xl mb-4">{t("dash_bookmarks")}</h2>
            <div className="rounded-xl border border-white/10 bg-card p-5" data-testid="dashboard-bookmarks">
              {(data?.bookmarks || []).length === 0 && <p className="text-mute text-sm">{t("dash_empty_bm")}</p>}
              <div className="flex flex-wrap gap-2">
                {(data?.bookmarks || []).map((b) => {
                  const s = STORIES.find((x) => x.id === b.chapter_id);
                  return (
                    <Link key={b.bm_id} to={`/storia/${b.chapter_id}?v=read`} title={b.note || undefined} data-testid={`dashboard-bookmark-${b.bm_id}`}
                      className="inline-flex items-center gap-1.5 rounded-full border border-gold/40 bg-gold/10 px-3.5 py-1.5 text-xs text-gold hover:bg-gold hover:text-black transition-colors">
                      <Bookmark className="w-3 h-3" /> {s ? chapterLabel(s, lang) : b.chapter_id} · {b.percent}%{b.note ? ` · “${b.note.slice(0, 24)}${b.note.length > 24 ? "…" : ""}”` : ""}
                    </Link>
                  );
                })}
              </div>
            </div>

            <h2 className="font-display font-bold text-xl mt-8 mb-4">{t("dash_my_payments")}</h2>
            <div className="rounded-xl border border-white/10 bg-card p-5 space-y-2" data-testid="dashboard-payments">
              {(data?.payments || []).length === 0 && <p className="text-mute text-sm">{t("dash_empty_pay")}</p>}
              {(data?.payments || []).map((p) => (
                <div key={p.session_id} className="flex items-center justify-between text-sm border-b border-white/5 pb-2">
                  <span className="flex items-center gap-2 text-mist">
                    <Heart className="w-3.5 h-3.5 text-ember" />
                    {p.lookup_key === "unlock_full" ? t("type_unlock") : t("type_donation")}
                  </span>
                  <span className="flex items-center gap-3">
                    <span className="font-semibold">{fmtAmount(p.amount, p.currency)}</span>
                    <span className={`font-mono2 text-[10px] uppercase ${p.payment_status === "paid" ? "text-gold" : "text-mute"}`}>{p.payment_status}</span>
                  </span>
                </div>
              ))}
            </div>
          </Reveal>
        </div>

        <Reveal>
          <h2 className="font-display font-bold text-xl mb-4">{t("dash_account")}</h2>
          <div className="grid sm:grid-cols-2 gap-6" data-testid="dashboard-account">
            <form onSubmit={changePw} className="rounded-xl border border-white/10 bg-card p-6 space-y-3">
              <p className="font-mono2 text-[10px] tracking-[0.25em] uppercase text-mute">{t("dash_change_pw")}</p>
              <input type="password" className={field} placeholder={t("dash_cur_pw")} data-testid="dash-current-password"
                value={pwForm.current} onChange={(e) => setPwForm({ ...pwForm, current: e.target.value })} />
              <input type="password" required minLength={6} className={field} placeholder={t("dash_new_pw")} data-testid="dash-new-password"
                value={pwForm.next} onChange={(e) => setPwForm({ ...pwForm, next: e.target.value })} />
              {pwMsg && <p className={`text-sm ${pwMsg.ok ? "text-gold" : "text-red-400"}`} data-testid="dash-pw-msg">{pwMsg.text}</p>}
              <button data-testid="dash-change-pw-button" className="rounded-full bg-ember px-6 py-2.5 text-xs font-semibold uppercase tracking-wider text-black hover:bg-gold transition-colors">
                {t("dash_change_pw")}
              </button>
            </form>
            <form onSubmit={changeEmail} className="rounded-xl border border-white/10 bg-card p-6 space-y-3">
              <p className="font-mono2 text-[10px] tracking-[0.25em] uppercase text-mute">{t("dash_change_email")}</p>
              <input type="email" required className={field} placeholder={t("dash_new_email")} data-testid="dash-new-email"
                value={emailForm.email} onChange={(e) => setEmailForm({ ...emailForm, email: e.target.value })} />
              <input type="password" className={field} placeholder={t("dash_pw_for_email")} data-testid="dash-email-password"
                value={emailForm.password} onChange={(e) => setEmailForm({ ...emailForm, password: e.target.value })} />
              {emailMsg && <p className={`text-sm ${emailMsg.ok ? "text-gold" : "text-red-400"}`} data-testid="dash-email-msg">{emailMsg.text}</p>}
              <button data-testid="dash-change-email-button" className="rounded-full bg-ember px-6 py-2.5 text-xs font-semibold uppercase tracking-wider text-black hover:bg-gold transition-colors">
                {t("dash_change_email")}
              </button>
              {emailPending && (
                <div className="pt-2 space-y-3 border-t border-white/10" data-testid="dash-email-confirm-block">
                  <p className="text-xs text-mute">{t("dash_email_confirm_hint")}</p>
                  <input className={`${field} text-center tracking-[0.4em] font-mono2`} placeholder="······" maxLength={6} inputMode="numeric"
                    data-testid="dash-email-code" value={emailCode} onChange={(e) => setEmailCode(e.target.value)} />
                  <button type="button" onClick={confirmEmail} data-testid="dash-email-confirm-button"
                    className="rounded-full border border-gold/50 px-6 py-2.5 text-xs font-semibold uppercase tracking-wider text-gold hover:bg-gold hover:text-black transition-colors">
                    {t("dash_email_confirm_btn")}
                  </button>
                </div>
              )}
            </form>
          </div>
        </Reveal>

        <Reveal>
          <h2 className="font-display font-bold text-xl mt-12 mb-2 flex items-center gap-2"><ShieldCheck className="w-5 h-5 text-gold" /> {t("dash_security")}</h2>
          <p className="text-mute text-xs mb-6">{t("dash_security_sub")}</p>
          <div className="grid sm:grid-cols-2 gap-6" data-testid="dashboard-security">
            <div className="rounded-xl border border-white/10 bg-card p-6 space-y-3">
              <p className="font-mono2 text-[10px] tracking-[0.25em] uppercase text-mute">{t("dash_rec_email")}</p>
              <p className="text-xs text-mute">{t("dash_rec_email_sub")}</p>
              {data?.recovery_email ? (
                <div className="flex items-center justify-between gap-3">
                  <p className="text-sm text-gold" data-testid="dash-recovery-email-current">{data.recovery_email}</p>
                  <button onClick={removeRecEmail} data-testid="dash-recovery-email-remove"
                    className="rounded-full border border-white/15 px-4 py-1.5 text-[10px] font-mono2 uppercase tracking-wider text-mist hover:text-red-400 hover:border-red-400/50 transition-colors">
                    {t("dash_remove")}
                  </button>
                </div>
              ) : (
                <>
                  <input type="email" className={field} placeholder={t("dash_rec_email")} data-testid="dash-recovery-email-input"
                    value={recEmail} onChange={(e) => setRecEmail(e.target.value)} />
                  {recAwaiting && (
                    <input className={`${field} text-center tracking-[0.4em] font-mono2`} placeholder="······" maxLength={6} inputMode="numeric"
                      data-testid="dash-recovery-code-input" value={recCode} onChange={(e) => setRecCode(e.target.value)} />
                  )}
                  {recMsg && <p className={`text-sm ${recMsg.ok ? "text-gold" : "text-red-400"}`} data-testid="dash-recovery-msg">{recMsg.text}</p>}
                  <button type="button" onClick={recAwaiting ? verifyRecCode : sendRecCode}
                    data-testid="dash-recovery-email-button"
                    className="rounded-full bg-ember px-6 py-2.5 text-xs font-semibold uppercase tracking-wider text-black hover:bg-gold transition-colors">
                    {recAwaiting ? t("dash_rec_verify") : t("dash_rec_send")}
                  </button>
                </>
              )}
            </div>

            <div className="rounded-xl border border-white/10 bg-card p-6 space-y-3">
              <p className="font-mono2 text-[10px] tracking-[0.25em] uppercase text-mute">{t("dash_phone")}</p>
              <p className="text-xs text-mute">{t("dash_phone_sub")}</p>
              {data?.phone ? (
                <div className="flex items-center justify-between gap-3">
                  <p className="text-sm text-gold" data-testid="dash-phone-current">{data.phone}</p>
                  <button onClick={removePhone} data-testid="dash-phone-remove"
                    className="rounded-full border border-white/15 px-4 py-1.5 text-[10px] font-mono2 uppercase tracking-wider text-mist hover:text-red-400 hover:border-red-400/50 transition-colors">
                    {t("dash_remove")}
                  </button>
                </div>
              ) : (
                <>
                  <input className={field} placeholder="+393331234567" data-testid="dash-phone-input"
                    value={phone} onChange={(e) => setPhone(e.target.value)} />
                  {phoneMsg && <p className={`text-sm ${phoneMsg.ok ? "text-gold" : "text-red-400"}`} data-testid="dash-phone-msg">{phoneMsg.text}</p>}
                  <button type="button" onClick={savePhone} data-testid="dash-phone-save"
                    className="rounded-full bg-ember px-6 py-2.5 text-xs font-semibold uppercase tracking-wider text-black hover:bg-gold transition-colors">
                    {t("dash_phone_save")}
                  </button>
                </>
              )}
            </div>
          </div>

          <div className="rounded-xl border border-white/10 bg-card p-6 mt-6" data-testid="dashboard-security-questions">
            <p className="font-mono2 text-[10px] tracking-[0.25em] uppercase text-mute">{t("dash_sq")}</p>
            <p className="text-xs text-mute mt-2 mb-4">{data?.security_set ? t("dash_sq_set") : t("dash_sq_sub")}</p>
            <div className="grid sm:grid-cols-2 gap-2 mb-4">
              {SQ_KEYS.map((k) => (
                <button key={k} type="button" onClick={() => toggleSq(k)} data-testid={`dash-sq-pick-${k}`}
                  className={`rounded-lg border px-4 py-2.5 text-xs text-left transition-colors ${sqPicks.includes(k) ? "border-gold bg-gold/10 text-gold" : "border-white/10 text-mist hover:border-white/30"}`}>
                  {t(k)}
                </button>
              ))}
            </div>
            <p className="font-mono2 text-[10px] tracking-widest text-mute mb-3 uppercase" data-testid="dash-sq-count">{sqPicks.length} / 5</p>
            <div className="space-y-3 mb-4">
              {sqPicks.map((k) => (
                <div key={k}>
                  <label className="block text-xs text-mute mb-1.5">{t(k)}</label>
                  <input className={field} placeholder={t("rec_answer_ph")} data-testid={`dash-sq-answer-${k}`}
                    value={sqAnswers[k] || ""} onChange={(e) => setSqAnswers({ ...sqAnswers, [k]: e.target.value })} />
                </div>
              ))}
            </div>
            {sqMsg && <p className={`text-sm mb-3 ${sqMsg.ok ? "text-gold" : "text-red-400"}`} data-testid="dash-sq-msg">{sqMsg.text}</p>}
            <button type="button" onClick={saveSq} disabled={sqPicks.length !== 5 || sqPicks.some((k) => !(sqAnswers[k] || "").trim())}
              data-testid="dash-sq-save"
              className="rounded-full bg-ember px-6 py-2.5 text-xs font-semibold uppercase tracking-wider text-black hover:bg-gold transition-colors disabled:opacity-40">
              {t("dash_sq_save")}
            </button>
          </div>
        </Reveal>
      </section>
    </main>
  );
};

export default Dashboard;
